import {
    Box,
    Button,
    Card,
    CardContent,
    capitalize,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {UseMutationResult} from "react-query";
import {Aufgabe, Prompt, PromptListe} from "../../api";
import {usePromptsByAufgabeId} from "../../client/queries";
import {useAufgabenPromptHandling} from "../../hooks/aufgaben/useAufgabenPromptHandling";
import {DEFAULT_LANGUAGE} from "../../config/language";
import DialogCreatePromptVersion from "./DialogCreatePromptVersion";

// ── shared helpers ────────────────────────────────────────────────────────────

interface Draft {
    versionName: string;
    versionDescription: string;
    prompt: string;
    foreignLanguagePrompt: string;
}

const draftFromPrompt = (p?: Prompt): Draft => ({
    versionName: p?.versionName ?? '',
    versionDescription: p?.versionDescription ?? '',
    prompt: p?.prompt ?? '',
    foreignLanguagePrompt: p?.foreignLanguagePrompt ?? '',
});

const isDirty = (a: Draft, b: Draft) =>
    a.versionName !== b.versionName ||
    a.versionDescription !== b.versionDescription ||
    a.prompt !== b.prompt ||
    a.foreignLanguagePrompt !== b.foreignLanguagePrompt;

export const versionLabel = (items: Prompt[], promptId: string): string => {
    const idx = items.findIndex(p => p.id === promptId);
    if (idx < 0) return '';
    return `v${items.length - idx}`;
};

// ── shared base component ─────────────────────────────────────────────────────

/** Minimal prompt-data shape accepted by the editor (AufgabePromptDaten, PhoneNumberPromptDaten, PromptDaten all satisfy this). */
export interface PromptDataShape {
    versionName: string;
    versionDescription?: string | null;
    prompt?: string | null;
    foreignLanguagePrompt?: string | null;
}

export interface PromptVersionEditorBaseProps {
    entityId: string;
    versions: PromptListe;
    activePromptId?: string | null;
    canEdit: boolean;
    sprache?: string;
    title: string;
    noneSelectedText: string;
    createPromptMutation: UseMutationResult<Prompt, unknown, {data: PromptDataShape; activate: boolean}>;
    updatePromptMutation: UseMutationResult<Prompt, unknown, {promptId: string; data: PromptDataShape}>;
    setActivePromptMutation: UseMutationResult<unknown, unknown, string>;
    deletePromptMutation: UseMutationResult<Prompt, unknown, string>;
    selectedPromptId?: string;
    onSelectPrompt?: (promptId: string) => void;
}

export function PromptVersionEditorBase({
    entityId,
    versions,
    activePromptId,
    canEdit,
    sprache,
    title,
    noneSelectedText,
    createPromptMutation,
    updatePromptMutation,
    setActivePromptMutation,
    deletePromptMutation,
    selectedPromptId,
    onSelectPrompt,
}: PromptVersionEditorBaseProps) {
    const {t: translation} = useTranslation('aufgaben');
    const {t: tLang} = useTranslation('sprachen');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    const [internalSelectedId, setInternalSelectedId] = useState<string>(selectedPromptId ?? activePromptId ?? '');
    const currentSelectedId = selectedPromptId ?? internalSelectedId;
    const setSelected = (id: string) => {
        if (onSelectPrompt) onSelectPrompt(id);
        else setInternalSelectedId(id);
    };

    const selected = useMemo(
        () => versions.items.find(v => v.id === currentSelectedId) ?? versions.items.find(v => v.id === activePromptId),
        [versions.items, currentSelectedId, activePromptId]
    );
    const isActive = !!activePromptId && selected?.id === activePromptId;

    const baseline = useMemo(() => draftFromPrompt(selected), [selected]);
    const [draft, setDraft] = useState<Draft>(baseline);
    const seededIdRef = useRef<string | undefined>(selected?.id);
    useEffect(() => {
        if (seededIdRef.current !== selected?.id) {
            seededIdRef.current = selected?.id;
            setDraft(baseline);
        }
    }, [selected?.id, baseline]);

    const dirty = isDirty(draft, baseline);
    const lang = sprache ?? DEFAULT_LANGUAGE;
    const showForeign = lang !== DEFAULT_LANGUAGE && lang.length === 5;

    const handleSave = () => {
        if (!selected) return;
        updatePromptMutation.mutate({
            promptId: selected.id,
            data: {
                versionName: draft.versionName.trim() || 'Initial',
                versionDescription: draft.versionDescription || undefined,
                prompt: draft.prompt,
                foreignLanguagePrompt: draft.foreignLanguagePrompt || undefined,
            },
        });
    };

    return (
        <>
        <Card>
            <CardContent>
                <Stack spacing={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">{capitalize(title)}</Typography>
                        <Button variant="contained" color="secondary" size="small" disabled={!canEdit} onClick={() => setCreateDialogOpen(true)}>
                            {capitalize(translation('prompt-version-create'))}
                        </Button>
                    </Box>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <TextField
                            select
                            label={capitalize(translation('prompt-version-select-label'))}
                            size="small"
                            sx={{minWidth: 280}}
                            value={selected?.id ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setSelected(e.target.value)}
                        >
                            {versions.items.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {versionLabel(versions.items, v.id)} – {v.versionName}
                                    {v.id === activePromptId ? ` (${translation('prompt-version-active')})` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        {isActive && <Chip color="success" size="small" label={capitalize(translation('prompt-version-active'))}/>}
                        <Box flexGrow={1}/>
                        <Button variant="contained" color="secondary" disabled={!canEdit || !dirty || updatePromptMutation.isLoading} onClick={handleSave}>
                            {capitalize(translation('prompt-version-save'))}
                        </Button>
                        <Button variant="outlined" disabled={!canEdit || isActive || setActivePromptMutation.isLoading || !selected} onClick={() => selected && setActivePromptMutation.mutate(selected.id)}>
                            {capitalize(translation('prompt-version-activate'))}
                        </Button>
                        <Button variant="outlined" color="error" disabled={!canEdit || isActive || deletePromptMutation.isLoading || !selected} onClick={() => selected && deletePromptMutation.mutate(selected.id)}>
                            {capitalize(translation('prompt-version-delete'))}
                        </Button>
                    </Box>
                    <Box display="grid" gridTemplateColumns={{xs: '1fr', md: '1fr 2fr'}} gap={2}>
                        <TextField label={capitalize(translation('prompt-version-name'))} value={draft.versionName} onChange={(e) => setDraft({...draft, versionName: e.target.value})} disabled={!canEdit} required size="small"/>
                        <TextField label={capitalize(translation('prompt-version-description'))} value={draft.versionDescription} onChange={(e) => setDraft({...draft, versionDescription: e.target.value})} disabled={!canEdit} size="small"/>
                    </Box>
                    <Box display="grid" gridTemplateColumns={showForeign ? {xs: '1fr', md: '1fr 1fr'} : '1fr'} gap={2}>
                        <TextField label={capitalize(translation('task-attributes-prompt'))} value={draft.prompt} onChange={(e) => setDraft({...draft, prompt: e.target.value})} disabled={!canEdit} multiline rows={20} fullWidth/>
                        {showForeign && (
                            <TextField label={`${capitalize(translation('task-attributes-prompt'))} (${tLang(lang)})`} value={draft.foreignLanguagePrompt} onChange={(e) => setDraft({...draft, foreignLanguagePrompt: e.target.value})} disabled={!canEdit} multiline rows={20} fullWidth/>
                        )}
                    </Box>
                    {!selected && (
                        <Typography color="text.secondary" variant="body2">{noneSelectedText}</Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
        <DialogCreatePromptVersion
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            entityId={entityId}
            sprache={sprache}
            createMutation={createPromptMutation}
            seedPrompt={selected}
            onCreated={(p) => { setSelected(p.id); }}
            skipActivePromptQuery
        />
        </>
    );
}

// ── thin wrapper for regular (outbound) prompt ────────────────────────────────

interface Props {
    aufgabe: Aufgabe;
    canEdit: boolean;
    selectedPromptId?: string;
    onSelectPrompt?: (promptId: string) => void;
}

export default function AufgabePromptVersionEditor({aufgabe, canEdit, selectedPromptId, onSelectPrompt}: Props) {
    const {t: translation} = useTranslation('aufgaben');
    const versions: PromptListe = usePromptsByAufgabeId(aufgabe.id);
    const {createPromptMutation, updatePromptMutation, setActivePromptMutation, deletePromptMutation} = useAufgabenPromptHandling(aufgabe.id);

    return (
        <PromptVersionEditorBase
            entityId={aufgabe.id}
            versions={versions}
            activePromptId={aufgabe.activePromptId}
            canEdit={canEdit}
            sprache={aufgabe.sprache}
            title={translation('prompt-versions-title')}
            noneSelectedText={translation('prompt-version-none-selected')}
            createPromptMutation={createPromptMutation}
            updatePromptMutation={updatePromptMutation}
            setActivePromptMutation={setActivePromptMutation}
            deletePromptMutation={deletePromptMutation}
            selectedPromptId={selectedPromptId}
            onSelectPrompt={onSelectPrompt}
        />
    );
}
